import React, { useEffect, useState, useRef } from "react";
import {
  Search,
  MoreVertical,
  ChevronsLeft,
  ChevronsRight,
  FileDown,
  UserPlus,
  CircleX,
} from "lucide-react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader";
import axios from "axios";
import * as XLSX from "xlsx";
import host from "../../global";
import { useNavigate } from "react-router-dom";

const JobLibrary = () => {
  const navigate = useNavigate();
  const [jobPositions, setJobPositions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredJobPositions, setFilteredJobPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedJobForInvite, setSelectedJobForInvite] = useState(null);
  const [shareableLink, setShareableLink] = useState("");
  const [linkExpiryDate, setLinkExpiryDate] = useState(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [jobStatuses, setJobStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [maxPageButtons] = useState(5);
  const dropdownContainerRef = useRef(null);
  const accessToken = Cookies.get("accessToken");

  useEffect(() => {
    const fetchJobPositions = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${host}/getjobpositionsdata`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const finalizedJobs = response.data?.job_descriptions.filter(
          (job) => !job.status || job.status !== "screened"
        );
        console.log("Fetched job positions:", JSON.stringify(finalizedJobs));
        setJobPositions(finalizedJobs);
        setFilteredJobPositions(finalizedJobs);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              Cookies.remove("accessToken");
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
      }
    };
    fetchJobPositions();
  }, [accessToken]);

  useEffect(() => {
    const filtered = jobPositions
      .filter((job) =>
        job.job_role.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => new Date(b.posting_date) - new Date(a.posting_date));
    setFilteredJobPositions(filtered);
    setCurrentPage(1);
    setOpenDropdownIndex(null);
  }, [searchQuery, jobPositions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(event.target)
      ) {
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const totalItems = filteredJobPositions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredJobPositions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setOpenDropdownIndex(null);
    }
  };

  const getPageRange = () => {
    const startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleDownloadExcel = () => {
    try {
      const data = filteredJobPositions.map((job) => ({
        "Job Role": job.job_role,
        "Created On": job.posting_date,
        Experience: job.experience,
        "No. of Application": job.application_count,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [{ wch: 55 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Job Vacancies");
      XLSX.writeFile(wb, "JobVacancies.xlsx");
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download Excel file. Please try again.");
      console.error("Excel download error:", error);
    }
  };

  const handleInviteCandidate = async (job) => {
    setSelectedJobForInvite(job);
    setIsGeneratingLink(true);
    setShowInviteModal(true);
    try {
      console.log("Job object:", job);
      if (!job.jd_id || !job.job_role) {
        throw new Error("Missing required job data: job_id or job_role");
      }
      const response = await axios.post(
        `${host}/generate-shareable-link`,
        {
          job_id: job.jd_id,
          job_role: job.job_role,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const baseUrl = window.location.origin;
      const shareableUrl = `${baseUrl}/apply/${response.data.link_token}`;
      setShareableLink(shareableUrl);
      setLinkExpiryDate(response.data.expires_at);

      if (response.data.status_changed) {
        setJobStatuses((prev) => ({
          ...prev,
          [job.jd_id]: "Active",
        }));
        toast.success(
          `Link created! Job status changed from "${response.data.original_status}" to "Active" to enable applications.`
        );
      }
    } catch (error) {
      console.error("Error generating shareable link:", error);
      if (error.response && error.response.status === 400) {
        console.log("Request payload was:", {
          job_id: job.jd_id,
          job_role: job.job_role,
        });
        console.log("Error response:", error.response.data);
      }
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail || "Unknown error";
        if (status === 401) {
          await new Promise((resolve) => {
            Cookies.remove("accessToken");
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 400) {
          toast.error(`Bad Request: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setSelectedJobForInvite(null);
    setShareableLink("");
    setLinkExpiryDate(null);
    setIsCopied(false);
  };

  const formatExpiryDate = (isoString) => {
    if (!isoString) return "Not available";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareableLink);
    setIsCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => {
      setShowInviteModal(false);
      setIsCopied(false);
    }, 2000);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen border rounded-2xl border-gray-700/50 glass-effect text-white">
      <div className="flex items-center justify-between py-6 px-12">
        <h1 className="text-xl text-[#D9D9D9] font-normal">Job Vacancies</h1>
        <div className="flex items-center space-x-4">
          <div className="relative flex items-center">
            <Search className="absolute left-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search job roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-600 rounded-full text-white text-sm focus:outline-none focus:ring-1 focus:ring-white transition-all"
            />
          </div>
          <div className="relative group flex items-center">
            <button onClick={handleDownloadExcel}>
              <FileDown className="w-6 h-6 text-[#C1A0D0]" />
            </button>
            <span className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 top-10 right-0 transform translate-x-1/2 text-center">
              Download Excel
            </span>
          </div>
        </div>
      </div>
      <div className="mx-4 overflow-x-auto" ref={dropdownContainerRef}>
        {filteredJobPositions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">
              No job roles found matching your search.
            </p>
          </div>
        ) : (
          <table className="w-full min-w-[640px] border-t border-[#5A4479D4] table-fixed">
            <thead>
              <tr className="border-b border-[#5A4479D4]">
                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#D8D8D8]">
                  Job Role
                </th>
                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#D8D8D8] hidden sm:table-cell">
                  Created On
                </th>
                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#D8D8D8]">
                  Experience
                </th>
                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#D8D8D8] hidden md:table-cell">
                  No. of Applications
                </th>
                <th className="text-left px-4 sm:px-6 py-4 text-xs font-medium text-[#D8D8D8] w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {currentItems.map((job, index) => (
                <tr
                  key={job.jd_id || index}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td
                    className="px-4 sm:px-6 py-4 cursor-pointer"
                    onClick={() =>
                      navigate("/hr/job-applicants", {
                        state: job,
                      })
                    }
                  >
                    <div className="text-[#CCCCCCE5] text-sm truncate">
                      {job?.job_role}
                    </div>
                  </td>
                  <td
                    className="px-4 sm:px-6 py-4 text-[#CCCCCCE5] text-sm hidden sm:table-cell cursor-pointer"
                    onClick={() =>
                      navigate("/hr/job-applicants", {
                        state: job,
                      })
                    }
                  >
                    {job?.posting_date}
                  </td>
                  <td
                    className="px-4 sm:px-6 py-4 text-[#CCCCCCE5] text-sm cursor-pointer"
                    onClick={() =>
                      navigate("/hr/job-applicants", {
                        state: job,
                      })
                    }
                  >
                    {`${job?.minExp} - ${job?.maxExp} yrs`}
                  </td>
                  <td
                    className="px-4 sm:px-6 py-4 text-[#CCCCCCE5] text-sm hidden md:table-cell cursor-pointer"
                    onClick={() =>
                      navigate("/hr/job-applicants", {
                        state: job,
                      })
                    }
                  >
                    {job.application_count > 0 ? job.application_count : "-"}
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex justify-end relative">
                      <button
                        className="p-1 hover:bg-white/10 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownIndex(
                            openDropdownIndex === job?.jd_id ? null : job?.jd_id
                          );
                        }}
                      >
                        <MoreVertical size={20} className="text-gray-400" />
                      </button>
                      {openDropdownIndex === job?.jd_id && (
                        <div className="absolute -top-3 right-5 mt-2 w-48 bg-gray-800/90 backdrop-blur-sm rounded-md shadow-lg z-10 border border-gray-700/50">
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:bg-gradient-to-r hover:from-[#FF60B6]/20 hover:to-[#2960FD]/20 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInviteCandidate(job);
                            }}
                          >
                            <UserPlus size={16} className="mr-2" />
                            Invite Candidate
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showInviteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-5 backdrop-blur-xl z-50">
          <div className="p-6 bg-gradient-to-br from-[#22346140] to-[#0b0a1f0d] rounded-lg shadow-2xl backdrop-blur-[500px] w-full max-w-xl relative border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[#C1A0D0] text-lg font-medium">
                Invite Candidate
              </h3>
              <button
                onClick={closeInviteModal}
                className="hover:text-opacity-80 text-white"
              >
                <CircleX className="w-5 h-5" />
              </button>
            </div>

            {selectedJobForInvite && (
              <div className="mb-4">
                <p className="text-white text-sm mb-2">
                  JOB ROLE :{" "}
                  <span className="text-white tracking-wider">
                    {selectedJobForInvite.job_role}{" "}
                  </span>
                </p>
              </div>
            )}

            {linkExpiryDate && (
              <div className="flex items-center space-x-2 mb-6 text-sm">
                <span className="dark:text-white text-textSecondary">Link expires:</span>
                <span className=" text-white">
                  {formatExpiryDate(linkExpiryDate)}
                </span>
              </div>
            )}

            {isGeneratingLink ? (
              <div className="flex items-center justify-center py-8">
                <Loader />
                <span className="ml-3 dark:text-gray-300 text-textSecondary">Generating link...</span>
              </div>
            ) : shareableLink ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    type="text"
                    value={shareableLink}
                    readOnly
                    className="w-2/3 bg-transparent border border-gray-600 text-white px-4 py-2 rounded-full text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="bg-gradient-to-r from-pink-500 to-blue-500 text-white px-4 py-2 rounded-full text-sm hover:opacity-90 transition-opacity w-1/3"
                  >
                    {isCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-400">
                  Failed to generate link. Please try again.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-end px-6 py-4">
        <span className="text-gray-400 text-sm mr-10">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, totalItems)} of {totalItems} Jobs
        </span>
        <div className="flex items-center space-x-2">
          <button
            className="p-2 rounded-full border border-gray-600 text-gray-400 hover:bg-gray-700/50 disabled:opacity-50"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          {getPageRange().map((page) => (
            <button
              key={page}
              className={`w-8 h-8 rounded-full text-sm font-medium ${
                page === currentPage
                  ? "text-[#D9D9D9] bg-gradient-to-r from-[#FF60B6] to-[#2960FD]"
                  : "border border-gray-600 text-gray-400 hover:bg-gray-700/50"
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          {totalPages > maxPageButtons &&
            currentPage < totalPages - Math.floor(maxPageButtons / 2) && (
              <>
                <span className="text-gray-400 px-2">...</span>
                <button
                  className="w-8 h-8 rounded-full border border-gray-600 text-gray-400 hover:bg-gray-700/50 text-sm"
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          <button
            className="p-2 rounded-full border border-gray-600 text-gray-400 hover:bg-gray-700/50 disabled:opacity-50"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobLibrary;
